// @flow
import {Unveil} from '@homeaway/react-unveil';
import gql from 'graphql-tag';
import {debounce} from 'lodash';
import React from 'react';
import HitCarouselContainer from '../HitCarouselContainer/HitCarouselContainer';
import HitInfo from '../HitInfo/HitInfo';
import {hitInfoFragments} from '../HitInfo/HitInfo.fragments';
// $FlowFixMe
import TripBoardButton from '../TripBoardButton/TripBoardButton';
import {PHOTOCAROUSEL_ADVANCE} from '../../utils/carouselEdapUtils';
// $FlowFixMe
import {VirtualTourBadge} from '../VirtualTourBadge/VirtualTourBadge';

import type {HitClickEventData} from '../../utils/hitEdapUtils';
import type {SearchParamsType} from '../../reducers/searchParams';
import type {ListingShape as HitInfoListingShape} from '../HitInfo/HitInfo';
import {Link} from '@homeaway/react-localization-components';

import './Hit.less';

export const fragments = {
    Listing: gql`
        fragment HitListing on Listing {
            virtualTourBadge {
                name
                id
                helpText
            }
            amenitiesBadges {
                name
                id
                helpText
            }
            multiUnitProperty
            images {
                altText
                c6_uri
                c9_uri
            }
            listingNamespace
            ...HitInfoListing
        }
        ${hitInfoFragments.Listing}
    `
};

export type BadgeShape = {
    id: string,
    name: string,
    helpText: ?string
};

export type ListingImage = {
    altText: ?string,
    c6_uri: ?string,
    c9_uri: ?string
};

export type UnitAvailabilityInfo = {
    status: string
};

export type ListingShape = HitInfoListingShape & {
    availabilityInfo: ?UnitAvailabilityInfo,
    virtualTourBadge: ?BadgeShape,
    multiUnitProperty: ?boolean,
    images: ?(ListingImage[]),
    thumbnailUrl: ?string
};

export type MouseLeaveShape = {
    listing: ListingShape,
    hitIndex: number,
    page?: number,
    pageSize?: number
};

export type Props = {
    hitIndex: number,
    isGoodBot?: boolean,
    isWithoutAnchor?: boolean,
    listing: ListingShape,
    onHitClick?: (HitClickEventData) => void,
    onMouseEnter?: (listingId: string) => void,
    onMouseLeave?: (MouseLeaveShape) => void,
    page?: number,
    pageSize?: number,
    inputParams: SearchParamsType,
    isMultiUnitHit?: boolean
};

const HIT_UNVEIL_THRESHOLD = 100;
const HIT_UNVEIL_MIN_HEIGHT = '232px';

function renderHit(props: Props) {
    const {
        onHitClick,
        onMouseEnter,
        onMouseLeave,
        hitIndex,
        listing,
        page,
        pageSize,
        inputParams,
        isMultiUnitHit
    } = props;

    const handleMouseEnter = () =>
        listing && onMouseEnter && onMouseEnter(listing.listingId);
    const handleMouseLeave = () =>
        listing &&
        !listing.multiUnitProperty &&
        onMouseLeave &&
        onMouseLeave({listing, hitIndex, page, pageSize});
    const onClick = () =>
        listing &&
        onHitClick &&
        onHitClick({listing, hitIndex, page, pageSize, inputParams});
    const onMouseTimeout = 100;

    return (
        listing && (
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
                className="Hit media-flex media-flex--left media-flex--xs"
                data-wdio="hit"
                role="listitem"
                hit-index={hitIndex}
                onMouseEnter={debounce(handleMouseEnter, onMouseTimeout)}
                onMouseLeave={debounce(handleMouseLeave, onMouseTimeout)}
            >
                <div className="HitCarousel Hit__thumbnail">
                    <span aria-hidden="true">
                        {listing.virtualTourBadge && (
                            <VirtualTourBadge
                                badge={listing.virtualTourBadge}
                                detailPageUrl={listing.detailPageUrl}
                                onBadgeClick={onClick}
                            />
                        )}
                        <div className="Hit__tripboardButton">
                            <TripBoardButton listing={listing} />
                        </div>
                        <Link href={listing.detailPageUrl}>
                            {(localizedUrl) => (
                                <HitCarouselContainer
                                    detailPageUrl={localizedUrl}
                                    edapAction={PHOTOCAROUSEL_ADVANCE}
                                    images={listing.images || []}
                                    onImageClick={onClick}
                                />
                            )}
                        </Link>
                    </span>
                </div>
                <Link
                    target={'_blank'}
                    className="media-flex__content"
                    href={listing.detailPageUrl}
                    onClick={onClick}
                    tabIndex="0"
                >
                    <HitInfo
                        listing={listing}
                        hitIndex={hitIndex}
                        isMultiUnitHit={isMultiUnitHit}
                    />
                </Link>
            </div>
        )
    );
}

export const hitDataErrors = (
    listing: ListingShape,
    isDesktop: boolean
): Array<string> => {
    const errors = [];

    // eslint-disable-next-line guard-for-in
    for (const key in listing) {
        const value = listing[key];

        switch (key) {
            case 'detailPageUrl':
            case 'images':
                if (!value || !value.length) {
                    errors.push(key);
                }
                break;
            case 'propertyMetadata':
                if (isDesktop) {
                    if (!value) {
                        errors.push(key);
                    } else if (!value.headline) {
                        errors.push(`${key}.headline`);
                    }
                }
                break;
            default:
                break;
        }
    }

    return errors;
};

export default function Hit(props: Props) {
    // Good Bots (deviceClassification = -25) get Hits with no Unveil
    if (props.isGoodBot) {
        return renderHit(props);
    }

    // Unveil for the masses
    return (
        <Unveil
            threshold={HIT_UNVEIL_THRESHOLD}
            minContainerHeight={HIT_UNVEIL_MIN_HEIGHT}
        >
            {renderHit(props)}
        </Unveil>
    );
}
